import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCookieValue } from './utils';

const daysOptions = [
  { value: 7, label: 'Last 7 Days' },
  { value: 30, label: 'Last 30 Days' },
  { value: 60, label: 'Last 60 Days' },
  { value: 90, label: 'Last 90 Days' },
  { value: 'custom', label: 'Custom Range' }
];

const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatPercent = (value, decimals = 3) => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'object' && value.value !== undefined) {
    return formatPercent(value.value, decimals);
  }
  const num = Number(value);
  if (Number.isNaN(num)) return 'N/A';
  return `${num.toFixed(decimals)}%`;
};

const formatCount = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'object' && value.value !== undefined) {
    return formatCount(value.value);
  }
  const num = Number(value);
  if (Number.isNaN(num)) return 'N/A';
  return num.toLocaleString();
};

const formatDay = (value) => {
  if (!value) return 'N/A';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value.value) return value.value;
  return String(value);
};

const parseNumeric = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object' && value.value !== undefined) {
    return Number(value.value);
  }
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

const statusColorMap = {
  'Meeting SLO': '#2e7d32',
  'Warning': '#ed6c02',
  'Below SLO': '#c62828'
};

export const SloSliMetrics = () => {
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState([]);
  const [serviceData, setServiceData] = useState([]);
  const [selectedService, setSelectedService] = useState('stylishop.com');
  const [selectedDays, setSelectedDays] = useState(30);
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const services = useMemo(() => {
    return summaryData.map((row) => row.service).filter(Boolean).sort();
  }, [summaryData]);

  const filteredSummaryData = useMemo(() => {
    if (!selectedService) {
      return summaryData;
    }
    return summaryData.filter((row) => row.service === selectedService);
  }, [summaryData, selectedService]);

  const chartData = useMemo(() => {
    if (!Array.isArray(serviceData) || serviceData.length === 0) return [];
    return serviceData
      .map((row) => ({
        day: formatDay(row.day),
        success: parseNumeric(row.daily_success_pct),
        errors4xx: parseNumeric(row.daily_4xx_pct),
        errors5xx: parseNumeric(row.daily_5xx_pct)
      }))
      .filter((row) => row.success !== null)
      .reverse();
  }, [serviceData]);

  const maxSuccess = useMemo(() => {
    if (chartData.length === 0) return 0;
    return Math.max(...chartData.map((row) => row.success || 0));
  }, [chartData]);

  const fetchData = async ({ viewType, service, days, startDate, endDate }) => {
    const accessToken = getCookieValue('accessToken');
    if (!accessToken) {
      navigate('/login');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    try {
      const params = {
        viewType,
        service: service || undefined
      };
      
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      } else {
        params.days = days;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/get-slo-sli-metrics`,
        {
          headers: { Authorization: accessToken },
          params
        }
      );

      if (response?.data?.status) {
        if (viewType === 'summary') {
          setSummaryData(Array.isArray(response.data.data) ? response.data.data : []);
        } else if (viewType === 'service') {
          setServiceData(Array.isArray(response.data.data) ? response.data.data : []);
        }
      } else {
        setErrorMessage(response?.data?.statusMsg || 'Unable to fetch SLO/SLI metrics');
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        navigate('/login');
        return;
      }
      setErrorMessage('Failed to fetch SLO/SLI metrics');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isCustomRange && startDate && endDate) {
      fetchData({ viewType: 'summary', startDate, endDate });
    } else if (!isCustomRange) {
      fetchData({ viewType: 'summary', days: selectedDays });
    }
  }, [selectedDays, isCustomRange, startDate, endDate]);

  useEffect(() => {
    if (!selectedService) {
      setServiceData([]);
      return;
    }
    if (isCustomRange && startDate && endDate) {
      fetchData({ viewType: 'service', service: selectedService, startDate, endDate });
    } else {
      fetchData({ viewType: 'service', service: selectedService, days: selectedDays });
    }
  }, [selectedService, selectedDays, isCustomRange, startDate, endDate]);

  return (
    <div className="slo-sli-container">
      <div className="slo-sli-header">
        <h2>SLO/SLI Metrics per Service</h2>
        <div className="slo-sli-controls">
          <label>
            Time Range:
            <select
              value={isCustomRange ? 'custom' : selectedDays}
              onChange={(e) => {
                const val = e.target.value;
                if (val === 'custom') {
                  setIsCustomRange(true);
                  const today = new Date();
                  const thirtyDaysAgo = new Date(today);
                  thirtyDaysAgo.setDate(today.getDate() - 30);
                  setStartDate(formatDate(thirtyDaysAgo));
                  setEndDate(formatDate(today));
                } else {
                  setIsCustomRange(false);
                  setSelectedDays(Number(val));
                }
              }}
            >
              {daysOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          {isCustomRange && (
            <>
              <label>
                Start Date:
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={endDate || formatDate(new Date())}
                  style={{ marginLeft: '6px', padding: '4px 6px' }}
                />
              </label>
              <label>
                End Date:
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  max={formatDate(new Date())}
                  style={{ marginLeft: '6px', padding: '4px 6px' }}
                />
              </label>
            </>
          )}
          <label>
            Service:
            <select
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
            >
              <option value="">All Services</option>
              {services.map((service) => (
                <option key={service} value={service}>{service}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {isLoading && (
        <div className="slo-sli-loading">Loading SLO/SLI metrics...</div>
      )}
      {errorMessage && (
        <div className="slo-sli-error">{errorMessage}</div>
      )}

      <div className="slo-sli-section">
        <h3>Service Summary</h3>
        <div className="slo-sli-table-wrapper">
          <table className="slo-sli-table">
            <thead>
              <tr>
                <th>Service</th>
                <th>Days w/ Data</th>
                <th>Success %</th>
                <th>4xx %</th>
                <th>5xx %</th>
                <th>Total Requests</th>
                <th>SLO Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredSummaryData.length === 0 && (
                <tr>
                  <td colSpan="7">No summary data available</td>
                </tr>
              )}
              {filteredSummaryData.map((row) => (
                <tr key={row.service}>
                  <td>{row.service}</td>
                  <td>{formatCount(row.days_with_data)}</td>
                  <td>{formatPercent(row.avg_success_pct)}</td>
                  <td>{formatPercent(row.avg_4xx_pct)}</td>
                  <td>{formatPercent(row.avg_5xx_pct)}</td>
                  <td>{formatCount(row.total_reqs)}</td>
                  <td style={{ color: statusColorMap[row.slo_status] || '#333', fontWeight: 600 }}>
                    {row.slo_status || 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedService && (
        <div className="slo-sli-section">
          <h3>Daily Metrics for {selectedService}</h3>
          <div className="slo-sli-chart-wrapper">
            <div className="slo-sli-chart">
              {chartData.length === 0 && (
                <div className="slo-sli-chart-empty">No daily data available</div>
              )}
              {chartData.map((row, index) => {
                const scaled = maxSuccess > 0 ? (row.success / maxSuccess) * 100 : 0;
                const height = row.success > 0 ? Math.max(2, scaled) : 0;
                const showLabel = chartData.length <= 14 || index % Math.ceil(chartData.length / 10) === 0;
                return (
                  <div key={`${row.day}-${index}`} className="slo-sli-bar">
                    <div
                      className="slo-sli-bar-fill"
                      style={{ height: `${height}%` }}
                      title={`${row.day}\nSuccess: ${formatPercent(row.success)}\n4xx: ${formatPercent(row.errors4xx)}\n5xx: ${formatPercent(row.errors5xx)}`}
                    />
                    {showLabel && <div className="slo-sli-bar-label">{row.day.slice(5)}</div>}
                  </div>
                );
              })}
            </div>
            <div className="slo-sli-chart-legend">
              <span>Daily Success % (scaled to max in range)</span>
            </div>
          </div>
          <div className="slo-sli-table-wrapper">
            <table className="slo-sli-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Success %</th>
                  <th>4xx %</th>
                  <th>5xx %</th>
                  <th>Success Requests</th>
                  <th>Total Requests</th>
                </tr>
              </thead>
              <tbody>
                {serviceData.length === 0 && (
                  <tr>
                    <td colSpan="6">No service data available</td>
                  </tr>
                )}
                {serviceData.map((row, index) => (
                  <tr key={`${row.service}-${row.day}-${index}`}>
                    <td>{formatDay(row.day)}</td>
                    <td>{formatPercent(row.daily_success_pct)}</td>
                    <td>{formatPercent(row.daily_4xx_pct)}</td>
                    <td>{formatPercent(row.daily_5xx_pct)}</td>
                    <td>{formatCount(row.success_reqs)}</td>
                    <td>{formatCount(row.total_reqs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
